// apps/spa/src/components/EmbedEditor/index.tsx
// Shared embed editor (EMBED-01/02/03/04). Mounted by:
//   * 03-03 reminder editor (this phase)
//   * Phase 4 slash command form (verbatim — barrel keeps imports stable)
//
// Renders 8 fields under shadcn FormField wrappers bound to RHF via
// useFormContext. The component does NOT bind a zodResolver — the parent
// FormProvider owns that. Live preview is a sibling component
// (DiscordEmbedPreview); the parent route owns the layout (side-by-side or
// tabbed). EmbedEditor only renders inputs.
//
// Field paths:
//   title, url, description, color (hex string in form state),
//   thumbnail.url, image.url, footer.text, footer.icon_url
// Pass `name='embed'` to nest under a parent form key (e.g. for 03-03 where
// the reminder form has `embed: { ... }` as one of its fields).

import { useFormContext, useWatch } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { CharCounter } from './CharCounter';
import { ColorPicker } from './ColorPicker';

import type { Embed } from '@guardian/shared';

// Barrel re-exports for Phase 4 ergonomics — the slash-command form can write
// `import { EmbedEditor, DiscordEmbedPreview, totalEmbedChars, embedSchema } from '@/components/EmbedEditor';`
export { embedSchema, type Embed } from '@guardian/shared';
export { DiscordEmbedPreview } from './DiscordEmbedPreview';

function fieldName(prefix: string | undefined, key: string): string {
  return prefix ? `${prefix}.${key}` : key;
}

export interface EmbedEditorProps {
  /** Optional dot-prefix for nesting under a parent form (e.g. 'embed' nests fields as 'embed.title'). */
  name?: string;
}

export function EmbedEditor({ name }: EmbedEditorProps) {
  const { control } = useFormContext();

  const titleValue =
    (useWatch({ control, name: fieldName(name, 'title') }) as
      | string
      | undefined) ?? '';
  const descriptionValue =
    (useWatch({ control, name: fieldName(name, 'description') }) as
      | string
      | undefined) ?? '';
  const footerTextValue =
    (useWatch({ control, name: fieldName(name, 'footer.text') }) as
      | string
      | undefined) ?? '';

  return (
    <div className="space-y-4" data-slot="embed-editor">
      {/* Title */}
      <FormField
        control={control}
        name={fieldName(name, 'title')}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-baseline justify-between">
              <FormLabel>Title</FormLabel>
              <CharCounter value={titleValue} max={256} />
            </div>
            <FormControl>
              <Input maxLength={256} {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* URL */}
      <FormField
        control={control}
        name={fieldName(name, 'url')}
        render={({ field }) => (
          <FormItem>
            <FormLabel>URL</FormLabel>
            <FormControl>
              <Input type="url" {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Description */}
      <FormField
        control={control}
        name={fieldName(name, 'description')}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-baseline justify-between">
              <FormLabel>Description</FormLabel>
              <CharCounter value={descriptionValue} max={4096} />
            </div>
            <FormControl>
              <Textarea rows={6} {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Color (ColorPicker is self-controlled via useController) */}
      <FormField
        control={control}
        name={fieldName(name, 'color')}
        render={() => (
          <FormItem>
            <FormLabel>Color</FormLabel>
            <ColorPicker name={fieldName(name, 'color')} />
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Thumbnail URL */}
      <FormField
        control={control}
        name={fieldName(name, 'thumbnail.url')}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Thumbnail URL</FormLabel>
            <FormControl>
              <Input type="url" {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Image URL */}
      <FormField
        control={control}
        name={fieldName(name, 'image.url')}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Image URL</FormLabel>
            <FormControl>
              <Input type="url" {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Footer text */}
      <FormField
        control={control}
        name={fieldName(name, 'footer.text')}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-baseline justify-between">
              <FormLabel>Footer text</FormLabel>
              <CharCounter value={footerTextValue} max={2048} />
            </div>
            <FormControl>
              <Input maxLength={2048} {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Footer icon URL */}
      <FormField
        control={control}
        name={fieldName(name, 'footer.icon_url')}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Footer icon URL</FormLabel>
            <FormControl>
              <Input type="url" {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export function totalEmbedChars(values: Partial<Embed>): number {
  let total = 0;
  total += values.title?.length ?? 0;
  total += values.description?.length ?? 0;
  total += values.footer?.text?.length ?? 0;
  for (const f of values.fields ?? []) {
    total += (f.name?.length ?? 0) + (f.value?.length ?? 0);
  }
  return total;
}
